/** Camera barcode/QR scanner button — appends scans to a callback. */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ScanBarcode } from "lucide-react";

export const ScanButton = ({ onScan, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
          data-testid={testid || "scan-btn"}
          className="text-[10px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
        <ScanBarcode className="h-3 w-3" /> Scan
      </button>
      {open && <ScannerModal onScan={onScan} onClose={() => setOpen(false)} />}
    </>
  );
};

function ScannerModal({ onScan, onClose }) {
  const videoRef = useRef(null);
  const [err, setErr] = useState(null);
  const [last, setLast] = useState(null);

  useEffect(() => {
    let stream, timer, stopped = false;
    const start = async () => {
      if (!("BarcodeDetector" in window)) {
        setErr("Camera barcode scanning is not supported by this browser — type or paste serials instead (USB scanners work in any text field).");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" } });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        const detector = new window.BarcodeDetector({
          formats: ["qr_code", "code_128", "code_39", "ean_13", "upc_a", "data_matrix"] });
        timer = setInterval(async () => {
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes.length) {
              const v = codes[0].rawValue?.trim();
              if (v && v !== last) {
                setLast(v);
                onScan(v);
                toast.success(`Scanned: ${v}`);
              }
            }
          } catch { /* frame not ready */ }
        }, 400);
      } catch (e) {
        setErr("Camera access denied or unavailable — type serials manually.");
      }
    };
    start();
    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [onScan, last]);

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-4"
          data-testid="scanner-modal">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[13px] font-heading font-semibold">Scan serial / barcode</div>
          <button onClick={onClose} className="text-slate-400">✕</button>
        </div>
        {err ? (
          <div className="text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">{err}</div>
        ) : (
          <>
            <video ref={videoRef} className="w-full rounded-md bg-black aspect-video" muted playsInline />
            <div className="text-[10px] text-slate-500 mt-2">
              Point the camera at a barcode/QR. Each new code is added automatically.
              {last && <span className="ml-1 font-mono text-emerald-700">Last: {last}</span>}
            </div>
          </>
        )}
        <div className="flex justify-end mt-3">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white">Done</button>
        </div>
      </div>
    </div>
  );
}

export default ScanButton;
